import React, { FC, useEffect, useState } from 'react';
import { formatDate, formatDeliveryTime, getDatePickerFormat } from 'utils/format';
import { Box, Button, Grid, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimelineIcon from '@mui/icons-material/Timeline';
import { LoadingButton } from '@mui/lab';
import { Order } from 'models/order';
import { updateProductionPlanTemplate, updateDeliveryDateTemplate } from 'apollo/templates/order';
import { updateInternalDeliveryDateTemplate } from 'apollo/templates/delivery-schedule';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { DataTableHeadCell, DataTableCell, DataTableValue } from 'components/shared/data-table/data-table.componet';
import ProductionScheduling from 'components/shared/production-scheduling/production-scheduling.component';
import PlanOverviewDialog from 'components/shared/production-scheduling/plan-overview-dialog/plan-overview-dialog.component';
import { ProductionPlanningStep } from 'models/production-planning';
import { DepartmentsProvider } from 'context/departments-context';
import { ProductionPlanningProvider } from 'context/production-planning-context';
import IntExtDeliveryDatePicker from 'components/orders/order-details/int-ext-delivery-date-picker.component';
import { WorkingDayBeforeCounter } from 'utils/internal-delivery-advance-working-date';
import {
    mapOrderPlanToSteps,
    stepsToProductionPlanInput,
    formatStepDateTime,
} from 'components/shared/production-scheduling/production-scheduling.utils';

type OrderDetailsSchedulingSectionProps = {
    order: Order;
    onOrderUpdate: (order: Order) => void;
};

const getInternalDaysAdvance = (clinicDate: Date | null, internalDate: Date | null): number | undefined => {
    if (!clinicDate || !internalDate) return undefined;
    return WorkingDayBeforeCounter(clinicDate, internalDate);
};

const OrderDetailsSchedulingSection: FC<OrderDetailsSchedulingSectionProps> = ({ order, onOrderUpdate }) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [steps, setSteps] = useState<ProductionPlanningStep[]>(() => mapOrderPlanToSteps(order));
    const [isEditing, setIsEditing] = useState(false);
    const [showPlanOverview, setShowPlanOverview] = useState(false);
    const hasScheduledSteps = steps.some((s) => s.startDate && s.deadline);
    const { showBoundary } = useDenthubErrorBoundary();

    const originalClinicDate = order.deliveryDetails?.date ? new Date(order.deliveryDetails.date) : null;
    const originalInternalDate = order.deliveryDetails?.internalDeliveryDate
        ? new Date(order.deliveryDetails.internalDeliveryDate)
        : null;

    const [clinicDeliveryDate, setClinicDeliveryDate] = useState<Date | null>(originalClinicDate);
    const [internalDeliveryDate, setInternalDeliveryDate] = useState<Date | null>(originalInternalDate);
    const [internalDaysAdvance, setInternalDaysAdvance] = useState<number | undefined>(() =>
        getInternalDaysAdvance(originalClinicDate, originalInternalDate),
    );

    useEffect(() => {
        if (!isEditing) {
            setSteps(mapOrderPlanToSteps(order));
            const clinic = order.deliveryDetails?.date ? new Date(order.deliveryDetails.date) : null;
            const internal = order.deliveryDetails?.internalDeliveryDate
                ? new Date(order.deliveryDetails.internalDeliveryDate)
                : null;
            setClinicDeliveryDate(clinic);
            setInternalDeliveryDate(internal);
            setInternalDaysAdvance(getInternalDaysAdvance(clinic, internal));
        }
    }, [order.productionPlan, order.deliveryDetails]);

    const handleClinicDateChange = (date: Date | null) => {
        setClinicDeliveryDate(date);
        // If internal date now exceeds the new clinic date, clear it
        if (date && internalDeliveryDate && internalDeliveryDate > date) {
            setInternalDeliveryDate(null);
            setInternalDaysAdvance(undefined);
        } else if (date && internalDeliveryDate) {
            setInternalDaysAdvance(WorkingDayBeforeCounter(date, internalDeliveryDate));
        }
    };

    const handleInternalDateChange = (date: Date | null) => {
        setInternalDeliveryDate(date);
        setInternalDaysAdvance(
            date && clinicDeliveryDate ? WorkingDayBeforeCounter(clinicDeliveryDate, date) : undefined,
        );
    };

    const [updateProductionPlan, { loading: savingPlan }] = useMutation(updateProductionPlanTemplate, {
        onError: (error) => showBoundary(error),
    });

    const [updateDeliveryDate, { loading: savingDeliveryDate }] = useMutation(updateDeliveryDateTemplate, {
        onError: (error) => showBoundary(error),
    });

    const [updateInternalDeliveryDate, { loading: savingInternalDate }] = useMutation(
        updateInternalDeliveryDateTemplate,
        {
            onError: (error) => showBoundary(error),
        },
    );

    const saving = savingPlan || savingDeliveryDate || savingInternalDate;

    const handleSave = async () => {
        const promises: Promise<any>[] = [];

        promises.push(
            updateProductionPlan({
                variables: {
                    orderId: order._id,
                    productionPlan: stepsToProductionPlanInput(steps),
                },
            }),
        );

        const clinicDateChanged = clinicDeliveryDate?.getTime() !== originalClinicDate?.getTime();
        if (clinicDateChanged && clinicDeliveryDate) {
            promises.push(
                updateDeliveryDate({
                    variables: {
                        orderId: order._id,
                        deliveryDate: clinicDeliveryDate,
                        // The time of day lives in its own field. Send it back so changing
                        // the date here cannot clear it (DD-3801).
                        time: {
                            hour: order.deliveryDetails?.time?.hour,
                            minute: order.deliveryDetails?.time?.minute,
                        },
                    },
                }),
            );
        }

        const internalDateChanged = internalDeliveryDate?.getTime() !== originalInternalDate?.getTime();
        if (internalDateChanged) {
            promises.push(
                updateInternalDeliveryDate({
                    variables: {
                        orderId: order._id,
                        internalDeliveryDate: internalDeliveryDate,
                    },
                }),
            );
        }

        await Promise.all(promises);
        setIsEditing(false);
        onOrderUpdate({
            ...order,
            deliveryDetails: {
                ...order.deliveryDetails,
                ...(clinicDateChanged && clinicDeliveryDate ? { date: clinicDeliveryDate.getTime() } : {}),
                ...(internalDateChanged ? { internalDeliveryDate: internalDeliveryDate as Date } : {}),
            },
            productionPlan: stepsToProductionPlanInput(steps),
        });
    };

    const handleCancel = () => {
        setSteps(mapOrderPlanToSteps(order));
        setClinicDeliveryDate(originalClinicDate);
        setInternalDeliveryDate(originalInternalDate);
        setInternalDaysAdvance(getInternalDaysAdvance(originalClinicDate, originalInternalDate));
        setIsEditing(false);
    };

    // DD-3801: IntExtDeliveryDatePicker takes a timestamp, and calling setHours on the
    // state object to produce one rewrote the clinic delivery date to midnight on every
    // edit-mode render — which the save then persisted. Convert on a copy.
    const clinicDeliveryDayStart = clinicDeliveryDate ? new Date(clinicDeliveryDate).setHours(0, 0, 0, 0) : undefined;
    const clinicDeliveryTime = formatDeliveryTime(order.deliveryDetails?.time);

    return (
        <ProductionPlanningProvider>
            <DepartmentsProvider>
                <Box>
                    {isEditing ? (
                        <>
                            <Grid container spacing={2} mb={3} alignItems="flex-start">
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label={translate('orders:DELIVERY_DATE')}
                                        format={getDatePickerFormat()}
                                        value={clinicDeliveryDate}
                                        onChange={handleClinicDateChange}
                                        slotProps={{
                                            textField: { fullWidth: true, margin: 'dense' },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <IntExtDeliveryDatePicker
                                        label={translate('orders:INTERNAL_DELIVERY_DATE')}
                                        selectedDate={internalDeliveryDate}
                                        deliveryDaysAdvance={internalDaysAdvance}
                                        clinicDeliveryDate={clinicDeliveryDayStart}
                                        onChangeDate={handleInternalDateChange}
                                    />
                                </Grid>
                            </Grid>

                            <ProductionScheduling
                                steps={steps}
                                onStepsChange={setSteps}
                                internalDeliveryDate={internalDeliveryDate}
                                disablePastDateWarnings
                                orderId={order._id}
                            />
                        </>
                    ) : (
                        <>
                            <Grid container spacing={2} alignItems="center" mb={2}>
                                <Grid item xs={12} sm={5}>
                                    <Typography variant="subtitle2">{translate('orders:DELIVERY_DATE')}</Typography>
                                    <Typography variant="body2">
                                        {clinicDeliveryDate ? formatDate(clinicDeliveryDate, 'date') : '-'}
                                        {clinicDeliveryTime ? ` ${clinicDeliveryTime}` : ''}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2">
                                        {translate('orders:INTERNAL_DELIVERY_DATE')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {internalDeliveryDate ? formatDate(internalDeliveryDate, 'datetime') : '-'}
                                    </Typography>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={3}
                                    display="flex"
                                    justifyContent="flex-end"
                                    gap={1}
                                    flexWrap="nowrap"
                                >
                                    {hasScheduledSteps && (
                                        <Button
                                            size="small"
                                            startIcon={<TimelineIcon />}
                                            onClick={() => setShowPlanOverview(true)}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            {translate('orders:SHOW_PLAN_OVERVIEW')}
                                        </Button>
                                    )}
                                    <Button
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => setIsEditing(true)}
                                        data-test="production-planning-section.edit-toggle"
                                    >
                                        {translate('common:EDIT')}
                                    </Button>
                                </Grid>
                            </Grid>

                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <DataTableHeadCell>{translate('common:STEP')}</DataTableHeadCell>
                                        <DataTableHeadCell width={100}>{translate('orders:HOURS')}</DataTableHeadCell>
                                        <DataTableHeadCell width={200}>
                                            {translate('orders:DEADLINE')}
                                        </DataTableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        const currentWorkflowStepId = order.workFlow?.currentStep?.stepId;
                                        const currentPlanStepOrder = steps.find(
                                            (s) => s.stepId === currentWorkflowStepId,
                                        )?.stepOrder;
                                        return steps.map((step, i) => {
                                            const isPassed =
                                                currentPlanStepOrder !== undefined &&
                                                (step.stepOrder ?? i) < currentPlanStepOrder;
                                            return (
                                                <tr key={step.stepId || i}>
                                                    <DataTableCell readonly>
                                                        <DataTableValue>
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                {isPassed && (
                                                                    <CheckCircleIcon
                                                                        fontSize="small"
                                                                        sx={{ color: 'success.main', fontSize: '1rem' }}
                                                                    />
                                                                )}
                                                                {step.stepName}
                                                            </Box>
                                                        </DataTableValue>
                                                    </DataTableCell>
                                                    <DataTableCell readonly>
                                                        <DataTableValue>
                                                            {step.estimatedProductionHours != null
                                                                ? Math.round(step.estimatedProductionHours * 100) / 100
                                                                : '-'}
                                                        </DataTableValue>
                                                    </DataTableCell>
                                                    <DataTableCell readonly>
                                                        <DataTableValue>
                                                            {formatStepDateTime(step.deadline)}
                                                        </DataTableValue>
                                                    </DataTableCell>
                                                </tr>
                                            );
                                        });
                                    })()}
                                </TableBody>
                            </Table>
                        </>
                    )}

                    {isEditing && (
                        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                            <Button size="small" onClick={handleCancel}>
                                {translate('common:CANCEL')}
                            </Button>
                            <LoadingButton size="small" variant="contained" loading={saving} onClick={handleSave}>
                                {translate('common:SAVE')}
                            </LoadingButton>
                        </Box>
                    )}

                    <PlanOverviewDialog
                        open={showPlanOverview}
                        onClose={() => setShowPlanOverview(false)}
                        steps={steps}
                        orderId={order._id}
                        withWorkflowActions
                        onStepChanged={() => onOrderUpdate(order)}
                    />
                </Box>
            </DepartmentsProvider>
        </ProductionPlanningProvider>
    );
};

export default OrderDetailsSchedulingSection;
